import { useState } from 'react'
import { RotateCw, Send, XIcon } from 'lucide-react'
import { toast } from 'sonner'

import { Input } from '@playbooks/ui/components/input'
import {
  ActionCardModel,
  FileQuestionNode,
  Flow,
  getActiveTagNames,
  NodeSearchService,
  QuestionData,
  SectionData,
  ViewMode,
} from '@playbooks/action-cards-utils/src'
import { cn } from '@playbooks/ui/lib/utils'
import { Sheet, SheetContent } from '@playbooks/ui/components/sheet'
import { Button } from '@playbooks/ui/components/button'
import { ActionCardsElement } from '@playbooks/action-cards'

import { getLocalData, clearLocalData } from '../services/storage'
import lockVisibleHeaders from '../utils/lockVisibleHeaders'
import LinkButton from './ui/LinkButton'
import AcceptPolicy from './AcceptPolicy'
import { useTranslation } from 'react-i18next'
import { trackSubmitted } from '../services/trackService'
import { TargetFrame } from '../constants/index'
import LabeledSwitch from './ui/LabeledSwitch'
import { WebPlayQuestionFileUploadStorageService } from '../services/WebPlayQuestionFileUploadStorageService'
import Score from './Score'
import WebPlayActionCardService from '../services/WebPlayActionCardService'
import getNodesVisibilityRules from '../services/nodesVisibilityRules'
import WebPlayTitleGenerator from '../services/WebPlayTitleGenerator'
import { checkCompletionKeywords } from '../services/completionKeywordsService'
import RedirectLinkReplacementProcessor from '../services/RedirectLinkReplacementProcessor'
import LoaderButton from './ui/LoaderButton'
import { QuestionFile, SubmissionService, WebPlaySettings } from '../types'
import { isColorDark } from '../utils/isColorDark'

interface SubmitFormProps {
  isOpen: boolean
  playbookId: string
  showScore: boolean
  viewMode: ViewMode
  isStorageEnabled: boolean
  totalScore: number
  webplaySettings: WebPlaySettings
  submissionService: SubmissionService
  completionKeywords: Set<string | null> | null
  actionCardsElementRef: React.RefObject<ActionCardsElement | null>
  isPolicyAccepted: boolean
  webPlayEmail: string | null
  isWebPlayEmailValid: boolean
  isInvalid: boolean
  isWebPlayEmailInputVisible: boolean
  setWebPlayEmail: (email: string) => void
  setIsPolicyAccepted: (accepted: boolean) => void
  setIsOpen: (open: boolean) => void
  setIsStorageEnabled: (enabled: boolean) => void
}

export default function SubmitForm({
  isOpen,
  viewMode,
  showScore,
  playbookId,
  totalScore,
  webplaySettings,
  isStorageEnabled,
  submissionService,
  completionKeywords,
  actionCardsElementRef,
  isPolicyAccepted,
  webPlayEmail,
  isWebPlayEmailValid,
  isInvalid,
  setWebPlayEmail,
  isWebPlayEmailInputVisible,
  setIsPolicyAccepted,
  setIsOpen,
  setIsStorageEnabled,
}: SubmitFormProps) {
  const { t } = useTranslation()

  const {
    playTerm,
    targetFrame,
    accentColor,
    headersCount,
    redirectLink,
    submitPrompt,
    loggedInUserId,
    playTitleTemplate,
    enableDocumentView,
  } = webplaySettings

  const isDarkColor = isColorDark(accentColor)

  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isFunnelView = viewMode === ViewMode.FUNNEL

  const isSendForbidden = isSubmitting || !isPolicyAccepted || !isWebPlayEmailValid

  const handleClick = async () => {
    if (!isFunnelView) {
      const slidesData = actionCardsElementRef.current?.getSlidesData?.()

      if (!slidesData) {
        return
      }

      const allUnfilledRequiredIds = Object.values(slidesData.slides || {}).flatMap(value => {
        if (!value?.isFilled) {
          return value?.requiredDataIds || []
        }
        return []
      })

      if (allUnfilledRequiredIds?.length || !slidesData.canProceed) {
        toast.warning(
          t(
            'submit.incompleteEmailFields',
            'You have incomplete email fields. Check the data you entered and try again.',
          ),
        )
        setIsOpen(false)
        actionCardsElementRef.current?.highlightElementIds(allUnfilledRequiredIds)

        return
      }
    }

    if (isSendForbidden) {
      return
    }

    setIsSubmitting(true)

    let createdPlayId
    let createdPlayToken
    const actionCard = await getLocalData(playbookId)

    if (!actionCard) {
      toast.error(t('submit.somethingWentWrong', 'Something went wrong. Please try again later.'))
      setIsSubmitting(false)
      return
    }

    try {
      const actionCardModel = new WebPlayActionCardService(actionCard).getActionCardModel()!

      actionCardModel.nodesVisibilityRules.deleteRange(getNodesVisibilityRules())
      actionCardModel.adjustActionCardState()

      const keywords = getActiveTagNames(actionCardModel.data)

      if (!checkCompletionKeywords(keywords, completionKeywords)) {
        setIsSubmitting(false)
        return
      }

      const lastActiveSeparator = actionCard.sections.find(
        (s: SectionData) => s.id === actionCard.lastActiveSeparator,
      )?.name
      const score = actionCard.score || 0
      lockVisibleHeaders(actionCard, headersCount)

      const title = new WebPlayTitleGenerator(playTerm || '', webPlayEmail || '', actionCardModel).generateTitle(
        playTitleTemplate || null,
      )

      trackSubmitted(playbookId, keywords)

      const { playId, fomToken } = await submissionService.createPlay(
        playbookId,
        title,
        keywords,
        score,
        actionCard.scoringDimensions || [],
        lastActiveSeparator,
        actionCard.actionCardFlow || Flow.PLAY,
        loggedInUserId?.toString() || '',
      )

      createdPlayId = playId
      createdPlayToken = fomToken

      console.log('Created Play:', playId)

      actionCard.isFlowHidden = false

      // currently FileUpload-questions of action card contains data connected with local storage: it is necessary to remove them to not to copy them to the Play
      const fileNodesSearchService = actionCardModel
        .getSearchService()
        .filter((x: unknown) => x instanceof FileQuestionNode)
      fileNodesSearchService.forEach(x => ((x.data as QuestionData<[]>).value = []))
      await uploadFiles(playId, fileNodesSearchService, fomToken)
      console.log('Files for file questions have been attached')

      await submissionService.attachActionCardToPlay(playbookId, playId, actionCard, fomToken)
      console.log('Action card file has been attached')

      await submissionService.sendPlayCreatedNotification(
        playbookId,
        playId,
        webPlayEmail || '',
        keywords,
        actionCardModel,
        fomToken,
      )

      console.log('Notification has been sent')

      try {
        if (redirectLink) {
          let targetRedirectLink = new RedirectLinkReplacementProcessor(
            actionCardModel,
            playTerm || '',
            webPlayEmail || '',
          ).process(redirectLink!)

          targetRedirectLink = completeRedirectLinkWithCustomerParameter(actionCardModel, targetRedirectLink)

          switch (targetFrame) {
            case TargetFrame.SameFrame:
              redirectToLink(window, targetRedirectLink)
              setIsSubmitting(false)
              return
            case TargetFrame.ParentFrame:
              redirectToLink(window.parent, targetRedirectLink)
              setIsSubmitting(false)
              return
            case TargetFrame.NewTab:
              window.parent.open(targetRedirectLink)
          }
        }

        setIsSubmitted(true)
        toast.success(t('submit.success', 'Your data has been submitted successfully'))
      } catch (error) {
        console.error(error)
      }
    } catch (e) {
      if (createdPlayId) {
        submissionService.rollbackPlayCreation(playbookId, createdPlayId, createdPlayToken)
      }
      console.error(e)
      toast.info(t('submit.error', 'Failed to submit your data. Please try again'))
    }

    setIsSubmitting(false)
  }

  const handleRestart = async () => {
    await clearLocalData(playbookId)
    window.location.assign(window.location.href)
  }

  const uploadFiles = async (projectId: string, fileNodesSearchService: NodeSearchService, fomToken: unknown) => {
    if (!playbookId) {
      return
    }

    const uploadPromises: Promise<{
      id: number
      name: string
      attachmentId?: number
      attachmentUrl?: string
      attachmentError?: string
    }>[] = []

    const filesData = await new WebPlayQuestionFileUploadStorageService(playbookId).getAll()

    filesData.forEach(fileData => {
      const promise = uploadFile(fileNodesSearchService, projectId, fileData, fomToken)
      uploadPromises.push(promise)
    })

    const uploadedFiles = await Promise.all(uploadPromises)

    if (uploadedFiles.some(x => !!x.attachmentError)) {
      throw new Error('Some files have not been attached to a play')
    }
  }

  function uploadFile(
    fileNodesSearchService: NodeSearchService,
    projectId: string,
    fileData: QuestionFile,
    fomToken: unknown,
  ) {
    return submissionService
      .attachFileToPlay(
        playbookId,
        projectId,
        fileData.header.id.toString(),
        fileData.header.name,
        fileData.file,
        fomToken,
      )
      .then(([id, url]: [number, string]) => {
        const fileNode = fileNodesSearchService.findById(fileData.questionId)
        fileNode?.data.value.push({ id, name: fileData.file.name, url })
        return { id: fileData.id, name: fileData.file.name, attachmentId: id, attachmentUrl: url }
      })
      .catch((error: unknown) => {
        console.error(error)
        return { id: fileData.id, name: fileData.file.name, attachmentError: (error as Error).message }
      })
  }

  // Remove blurring on the last slide in the funnel view mode
  // when the submit form opens
  const handleRemoveOverlayClasses = (element: HTMLDivElement | null) => {
    if (!isFunnelView) return

    const overlay = element?.parentElement?.querySelector('[data-slot="sheet-overlay"]')
    overlay?.classList.remove('supports-backdrop-filter:backdrop-blur-xs')
    overlay?.classList.remove('bg-black/10')
  }

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        ref={handleRemoveOverlayClasses}
        showCloseButton={false}
        side="bottom"
        className="data-[side=bottom]:max-h-[50vh] data-[side=top]:max-h-[50vh]"
      >
        <Button variant="ghost" className="absolute top-2 right-2" size="icon-sm" onClick={() => setIsOpen(false)}>
          <XIcon />
          <span className="sr-only">Close</span>
        </Button>

        <div className={cn('flex flex-col gap-2 py-8 bg-background dark:border-t-transparent')}>
          <div className={`px-7 border-b dark:border-border pb-4`}>
            {enableDocumentView && showScore && (
              <div className={'flex items-center justify-end font-bold py-2'}>
                <div className={'mr-2'}>{t('submit.totalScoreLabel', 'Total score')}:</div>
                <Score value={totalScore} className={'border-2'} />
              </div>
            )}

            {!isFunnelView && <div className="relative text-sm mb-5 max-h-48 overflow-auto">{submitPrompt}</div>}

            <LabeledSwitch
              id="wp-remember-data"
              checked={isStorageEnabled}
              text={t('submit.saveInfoLabel', 'Remember my information')}
              onChange={checked => {
                setIsStorageEnabled(checked)
              }}
            />
          </div>

          <div className={`px-7`}>
            <div className="flex flex-col gap-4">
              <EmailInput
                isInvalid={isInvalid}
                webPlayEmail={webPlayEmail}
                setWebPlayEmail={setWebPlayEmail}
                isVisible={isWebPlayEmailInputVisible}
              />
              <div className={'mb-4'}>
                <AcceptPolicy
                  isPolicyAccepted={isPolicyAccepted}
                  privacyUrl={webplaySettings.privacyUrl}
                  setIsPolicyAccepted={setIsPolicyAccepted}
                />
              </div>
            </div>
            <div
              className="flex gap-2 items-center justify-center max-[410px]:flex-col max-[410px]:gap-0"
              title={!isPolicyAccepted ? t('submit.tooltip', 'Please accept the terms') : ''}
            >
              {!isSubmitted && (
                <LoaderButton
                  onClick={handleClick}
                  loading={isSubmitting}
                  icon={<Send size="16" />}
                  disabled={isSendForbidden}
                  label={t('submit.send', 'Send')}
                  accentColor={webplaySettings.accentColor}
                  className={cn(isDarkColor && 'border-zinc-800! border')}
                />
              )}

              {isSubmitted && (
                <>
                  <LoaderButton
                    onClick={handleClick}
                    loading={isSubmitting}
                    icon={<Send size="16" />}
                    disabled={isSendForbidden}
                    label={t('submit.resend', 'Resend')}
                    accentColor={webplaySettings.accentColor}
                    className={cn(isDarkColor && 'border-zinc-800! border')}
                  />
                  <LinkButton
                    onClick={handleRestart}
                    icon={<RotateCw size="16" />}
                    label={t('submit.restart', 'Restart')}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

const EmailInput = ({
  webPlayEmail,
  setWebPlayEmail,
  isVisible,
  isInvalid,
}: {
  webPlayEmail: string | null
  setWebPlayEmail: (email: string) => void
  isVisible: boolean
  isInvalid: boolean
}) => {
  const { t } = useTranslation()

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWebPlayEmail(e.currentTarget.value)
  }

  return (
    <div className={cn('flex justify-center transition-all', isVisible ? 'h-[75px]' : 'h-0 overflow-hidden')}>
      <div className="flex flex-col pt-1 w-full">
        <label htmlFor="emailInput" className="text-sm mb-1">
          E-Mail
        </label>
        <Input
          aria-invalid={isInvalid}
          onChange={handleChange}
          placeholder={t('submit.enterEmailLabel', 'Please enter your email')}
          id="emailInput"
          value={webPlayEmail ?? ''}
          className="w-full"
          type="text"
        />
      </div>
    </div>
  )
}

function redirectToLink(src: Window, link: string) {
  // We get the error (SecurityError: Failed to read a named property 'assign' from 'Location': Blocked a frame with origin)
  // if we try to assign location with location.assign(link) in the iframe.
  // The solution is to set location.href directly
  // Also we need to put this logic in a separate function because Eslint rule of
  // (https://react.dev/reference/rules/components-and-hooks-must-be-pure#side-effects-must-run-outside-of-render)
  src.location = link
}

function completeRedirectLinkWithCustomerParameter(actionCardModel: ActionCardModel, redirectLink: string) {
  /* it is temp code for one specific action card and it will be deleted soon (#14298) */
  const valueId = actionCardModel.getSearchService().findById(1181131805)?.getValue()?.id

  if (valueId) {
    const separator = redirectLink.includes('?') ? '&' : '?'

    switch (valueId) {
      case 3944468703:
        return `${redirectLink}${separator}audience=employee`
      case 2035047549:
        return `${redirectLink}${separator}audience=customer`
    }
  }

  return redirectLink
}
